/**
 * Costs by Sport Report Page
 *
 * Shows labor costs grouped by sport.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCostsBySport, downloadReportCSV, CostByCategoryData } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, useReportDateRange } from '../../../components/reports';

export default function CostsBySportPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [report, setReport] = useState<CostByCategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getCostsBySport({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setReport(response.report);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadReportCSV(
        '/reports/costs-by-sport',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        'costs-by-sport.csv'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  // Calculate totals
  const totals = report.reduce(
    (acc, row) => ({
      totalCost: acc.totalCost + row.totalCost,
      totalHours: acc.totalHours + row.totalHours,
      eventCount: acc.eventCount + row.eventCount,
      assignmentCount: acc.assignmentCount + row.assignmentCount,
    }),
    { totalCost: 0, totalHours: 0, eventCount: 0, assignmentCount: 0 }
  );

  return (
    <div>
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Costs by Sport</h1>
        <p className="text-gray-600 mt-1">Labor costs grouped by sport category</p>
      </div>

      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : report.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">No cost data by sport for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Events</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Assignments</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.map((row) => (
                  <tr key={row.category} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {row.category || 'Uncategorized'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.eventCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.assignmentCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{row.totalHours.toFixed(1)}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(row.totalCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">{totals.eventCount}</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">{totals.assignmentCount}</td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">{totals.totalHours.toFixed(1)}</td>
                  <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">{formatCurrency(totals.totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
